"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import { TextAnimate } from "./TextAnimation";
import SectionBadge from "./SectionBadge";
import { motion } from "motion/react";
import type { PlatformSectionData } from "@/types/cms";
import { AuroraText } from "./ui/aurora-text";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

interface WhatIsCountryClubSectionProps {
  data?: PlatformSectionData;
}

export default function WhatIsCountryClubSection({
  data,
}: WhatIsCountryClubSectionProps) {
  const sectionRef = useSectionTracking("Platform Section", "platform");
  const [isDesktop, setIsDesktop] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsDesktop(width >= 1024);
      setIsMobile(width < 768);
    };

    checkDevice();

    // Throttle resize events to reduce performance impact
    let timeoutId: NodeJS.Timeout;
    const throttledResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkDevice, 150);
    };

    window.addEventListener("resize", throttledResize);
    return () => {
      window.removeEventListener("resize", throttledResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const badgeText = data?.badgeText || "Market Intelligence";
  const title = data?.title || "Your Conference, *Mapped to the Market*";
  const description =
    data?.description ||
    "Most matchmaking asks attendees what they want.\n\nWe already know.\n\nBy mapping your entire target market — every company, every role, every need — we understand your attendees before they fill out a single form. We know who should meet whom, and why.\n\nThis intelligence powers every match. And it gives you something you've never had: a strategic view of your conference as a live market map.";
  const featureTags = data?.featureTags || [
    { id: 1, text: "Market Mapping" },
    { id: 2, text: "Live Intelligence" },
    { id: 3, text: "Strategic Insights" },
    { id: 4, text: "Real-time Data" },
  ];

  // Parse title to support *highlight* syntax - memoized for performance
  const parseTitle = useCallback(
    (text: string) => {
      const parts = text.split(/(\*[^*]+\*)/g);
      return parts.map((part, index) => {
        if (part.startsWith("*") && part.endsWith("*")) {
          // This is a highlighted part
          const content = part.slice(1, -1); // Remove * from both sides
          // Desktop: Aurora animation, Mobile: Static text
          return isDesktop ? (
            <AuroraText key={index}>{content}</AuroraText>
          ) : (
            <span
              key={index}
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #D4A84B, #E5C06D, #D4A84B)",
              }}
            >
              {content}
            </span>
          );
        }
        // Regular text - preserve line breaks
        return part.split("\n").map((line, lineIndex, array) => (
          <span key={`${index}-${lineIndex}`}>
            {line}
            {lineIndex < array.length - 1 && <br />}
          </span>
        ));
      });
    },
    [isDesktop],
  );

  // Memoize animation configs for better performance
  const glowAnimation = useMemo(
    () =>
      isMobile
        ? {}
        : {
            animate: { scale: [1, 1.05, 1] },
            transition: {
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut" as const,
            },
          },
    [isMobile],
  );

  const crownAnimation = useMemo(
    () =>
      isMobile
        ? {}
        : {
            animate: { y: [0, -8, 0] },
            transition: {
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut" as const,
            },
          },
    [isMobile],
  );
  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="platform"
      className="relative bg-linear-to-b from-black via-neutral-900 to-black py-24 px-4 lg:px-16 font-host-grotesk overflow-hidden"
    >
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to right, #3a3a3a 1px, transparent 1px),
                           linear-gradient(to bottom, #3a3a3a 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative max-w-330 mx-auto">
        {/* Section Badge */}
        <div className="flex justify-center mb-16">
          <SectionBadge text={badgeText} />
        </div>

        {/* Main Content - Split Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Image Showcase */}
          <div className="relative">
            <div className="relative bg-neutral-950 w-full p-4 md:p-8 aspect-square rounded-2xl overflow-hidden border border-white/10">
              <Image
                src={data?.sectionImage?.url || "/illustrations/universe.jpg"}
                alt={data?.sectionImageAlt || "Universe"}
                fill
                className="object-cover scale-95"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Right Side - Text Content */}
          <motion.div
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="text-left lg:text-left"
          >
            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl mb-6 text-white"
            >
              {parseTitle(title)}
            </motion.h2>

            {/* Description - Optimized for mobile */}

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-white/60 text-lg md:text-xl lg:text-2xl leading-relaxed font-normal mb-8 whitespace-pre-line"
            >
              {description}
            </motion.p>

            {/* Feature Tags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap gap-3"
            >
              {featureTags.map((tag, idx) => {
                const colors = [
                  {
                    bg: "bg-[#D4A84B]/10",
                    border: "border-[#D4A84B]/30",
                    text: "text-[#D4A84B]",
                  },
                  {
                    bg: "bg-[#D4A84B]/10",
                    border: "border-[#D4A84B]/30",
                    text: "text-[#D4A84B]",
                  },
                  {
                    bg: "bg-[#D4A84B]/10",
                    border: "border-[#D4A84B]/30",
                    text: "text-[#D4A84B]",
                  },
                  {
                    bg: "bg-[#D4A84B]/10",
                    border: "border-[#D4A84B]/30",
                    text: "text-[#D4A84B]",
                  },
                ];
                const color = colors[idx % colors.length];
                return (
                  <div
                    key={tag.id}
                    className={`px-4 py-2 ${color.bg} border ${color.border} rounded-full`}
                  >
                    <span className={`${color.text} text-sm font-medium`}>
                      {tag.text}
                    </span>
                  </div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
